"use client";

import {
  VStack,
  Text,
  Editable,
  IconButton,
  Badge,
  Stack,
} from "@chakra-ui/react";
import { memo, useState } from "react";
import GenericIcon from "../actions/GenericIcon";

type GenericEditableProps = {
  label: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onClear?: () => void;
  disabled?: boolean;
  error?: string | null;
  isTextarea?: boolean;
  onBlur?: () => void;
  handleKeyDown?: (e: React.KeyboardEvent, submitFn: () => void) => void;
};

//!!!! Watch out: Component has internal state, use key prop when using in lists to reset state !!!!

function GenericEditableInner({
  label,
  placeholder = "...",
  value,
  onChange,
  onSubmit,
  onClear,
  disabled = false,
  error,
  isTextarea = false,
  onBlur,
  handleKeyDown,
}: GenericEditableProps) {
  const [localValue, setLocalValue] = useState(value);

  return (
    <Stack
      align={"start"}
      alignItems="flex-center"
      direction={{ base: "column", sm: "row" }}
      w="full"
    >
      <Badge variant="surface" colorPalette="blue">
        {label}
      </Badge>

      <VStack align="stretch" w="full">
        <Editable.Root
          value={localValue}
          placeholder={placeholder}
          onChange={(event) => {
            const newValue = (event.target as HTMLInputElement).value;
            setLocalValue(newValue);
            onChange(newValue);
          }}
          onSubmit={onSubmit}
          disabled={disabled}
        >
          <Editable.Preview />
          {isTextarea ? (
            <Editable.Textarea
              minH="24"
              onKeyDown={(e) => handleKeyDown && handleKeyDown(e, onSubmit)}
              onBlur={onBlur}
            />
          ) : (
            <Editable.Input
              onKeyDown={(e) => handleKeyDown && handleKeyDown(e, onSubmit)}
              onBlur={onBlur}
            />
          )}
          <Editable.Control>
            <Editable.EditTrigger asChild>
              <IconButton
                variant="ghost"
                size="xs"
                aria-label={`Edit ${label}`}
                title={`Edit ${label}`}
              >
                <GenericIcon icon="edit" />
              </IconButton>
            </Editable.EditTrigger>
            <Editable.CancelTrigger asChild>
              <IconButton
                variant="outline"
                size="xs"
                onClick={() => {
                  setLocalValue("");
                  onClear?.();
                }}
                aria-label={`Clear ${label}`}
                title={`Clear ${label}`}
              >
                <GenericIcon icon="reset" />
              </IconButton>
            </Editable.CancelTrigger>
            <Editable.SubmitTrigger asChild>
              <IconButton
                variant="outline"
                size="xs"
                onClick={onSubmit}
                aria-label={`Save ${label}`}
                title={`Save ${label}`}
              >
                <GenericIcon icon="confirm" />
              </IconButton>
            </Editable.SubmitTrigger>
          </Editable.Control>
        </Editable.Root>

        {error && (
          <Text fontSize="sm" color="red.500">
            {error}
          </Text>
        )}
      </VStack>
    </Stack>
  );
}

// Wrap in React.memo for performance optimization
const GenericEditable = memo(GenericEditableInner);
export default GenericEditable;
